// 统一错误处理工具
// 处理 API 错误并提供用户友好的错误信息

import { message } from 'antd'
import type { AxiosError } from 'axios'

/**
 * API 错误接口
 */
export interface ApiError {
  code: string
  message: string
  details?: Record<string, unknown>
}

/**
 * 错误类型枚举
 */
export enum ErrorType {
  NETWORK = 'NETWORK',
  SERVER = 'SERVER',
  VALIDATION = 'VALIDATION',
  NOT_FOUND = 'NOT_FOUND',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  UNKNOWN = 'UNKNOWN',
}

/**
 * 获取错误类型
 */
export const getErrorType = (error: AxiosError<ApiError>): ErrorType => {
  if (error.code === 'ERR_NETWORK') {
    return ErrorType.NETWORK
  }

  const status = error.response?.status
  switch (status) {
    case 400:
      return ErrorType.VALIDATION
    case 401:
      return ErrorType.UNAUTHORIZED
    case 403:
      return ErrorType.FORBIDDEN
    case 404:
      return ErrorType.NOT_FOUND
    case 500:
    case 502:
    case 503:
      return ErrorType.SERVER
    default:
      return ErrorType.UNKNOWN
  }
}

/**
 * 获取用户友好的错误消息
 */
export const getErrorMessage = (errorType: ErrorType, defaultMessage: string): string => {
  const errorMessages: Record<ErrorType, string> = {
    [ErrorType.NETWORK]: '网络连接失败，请检查网络设置',
    [ErrorType.SERVER]: '服务器错误，请稍后重试',
    [ErrorType.VALIDATION]: '请求参数错误',
    [ErrorType.NOT_FOUND]: '请求的资源不存在',
    [ErrorType.UNAUTHORIZED]: '未授权，请重新登录',
    [ErrorType.FORBIDDEN]: '没有权限执行此操作',
    [ErrorType.UNKNOWN]: defaultMessage,
  }

  return errorMessages[errorType] || defaultMessage
}

/**
 * 统一错误处理函数
 * @param error 错误对象
 * @param defaultMessage 默认错误消息
 * @param messageApi Ant Design message API 实例
 */
export const handleApiError = (
  error: unknown,
  defaultMessage: string,
  messageApi?: { error: (msg: string) => void }
): ErrorType => {
  let errorType: ErrorType = ErrorType.UNKNOWN
  let displayMessage = defaultMessage

  if (isAxiosError(error)) {
    errorType = getErrorType(error)

    // 尝试从响应中获取详细错误信息
    const apiError = error.response?.data as ApiError | undefined
    if (apiError?.message) {
      displayMessage = `${defaultMessage}: ${apiError.message}`
    } else {
      displayMessage = getErrorMessage(errorType, defaultMessage)
    }
  } else if (error instanceof Error) {
    displayMessage = error.message || defaultMessage
  }

  if (messageApi) {
    messageApi.error(displayMessage)
  }

  return errorType
}

/**
 * 类型守卫：检查是否为 Axios 错误
 */
export function isAxiosError(error: unknown): error is AxiosError<ApiError> {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  )
}

/**
 * 可重试的错误类型
 */
const RETRYABLE_ERROR_TYPES: Set<ErrorType> = new Set([
  ErrorType.NETWORK,
  ErrorType.SERVER,
])

/**
 * 判断错误是否可重试
 */
export const isRetryableError = (errorType: ErrorType): boolean => {
  return RETRYABLE_ERROR_TYPES.has(errorType)
}

/**
 * 带重试的请求包装器
 * @param fn 要执行的异步函数
 * @param config 重试配置
 */
export interface RetryConfig {
  maxRetries?: number
  retryDelay?: number
  onRetry?: (error: Error, retryCount: number) => void
  shouldRetry?: (error: Error) => boolean
}

export const withRetry = async <T>(
  fn: () => Promise<T>,
  config: RetryConfig = {}
): Promise<T> => {
  const {
    maxRetries = 3,
    retryDelay = 1000,
    onRetry,
    shouldRetry,
  } = config

  let lastError: Error

  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error as Error

      // 检查是否应该重试
      const errorType = isAxiosError(error)
        ? getErrorType(error)
        : ErrorType.UNKNOWN

      const canRetry =
        i < maxRetries &&
        (shouldRetry ? shouldRetry(lastError) : isRetryableError(errorType))

      if (!canRetry) {
        throw lastError
      }

      // 调用重试回调
      if (onRetry) {
        onRetry(lastError, i + 1)
      }

      // 指数退避
      await new Promise((resolve) =>
        setTimeout(resolve, retryDelay * Math.pow(2, i))
      )
    }
  }

  throw lastError!
}
